import { Alert, Badge, Card, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconCircleMinus } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

/**
 * Can the platform actually onboard anyone right now?
 *
 * The API has always known — `configReason` distinguishes "no Meta app" from
 * "no Embedded Signup config" — but that diagnosis only ever reached a vendor,
 * who cannot act on it and was being told to open this very console. This is
 * the same answer pointed at the person who can fix it, with the number of
 * vendors waiting behind it.
 *
 * Three severities, and the difference matters. A missing Instagram config is
 * *degraded* — that panel falls back to the server-side redirect, which still
 * works — while a missing Embedded Signup config blocks every vendor. Calling
 * both "blocked" would teach the operator to ignore the panel.
 */
type Severity = 'ok' | 'degraded' | 'blocked';

const ICON: Record<Severity, React.ReactNode> = {
  ok: <IconCircleCheck size={16} />,
  degraded: <IconCircleMinus size={16} />,
  blocked: <IconAlertTriangle size={16} />,
};

const COLOR: Record<Severity, string> = {
  ok: 'green',
  degraded: 'gray',
  blocked: 'red',
};

function CheckRow({
  severity,
  label,
  detail,
}: {
  severity: Severity;
  label: string;
  detail: string;
}) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon size={22} radius="xl" variant="light" color={COLOR[severity]}>
        {ICON[severity]}
      </ThemeIcon>
      <Stack gap={0}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {detail}
        </Text>
      </Stack>
    </Group>
  );
}

export function PlatformReadinessPanel() {
  const readiness = trpc.integrations.admin.readiness.useQuery();

  if (readiness.isPending) return <Skeleton height={220} radius="md" />;
  if (readiness.isError || !readiness.data) return null;

  const r = readiness.data;
  const blocked = !r.hasAppId || !r.hasAppSecret || !r.hasEsConfigId;

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group gap="xs">
          <Text fw={600}>Platform readiness</Text>
          <Badge variant="light" color={blocked ? 'red' : 'green'}>
            {blocked ? 'Vendors cannot connect' : 'Ready'}
          </Badge>
        </Group>

        {blocked && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              {r.vendorsBlocked > 0
                ? `${r.vendorsBlocked} of ${r.vendorsTotal} vendors have no channel connected, and cannot connect one until this is fixed.`
                : 'No vendor can complete WhatsApp onboarding until this is fixed.'}
            </Text>
          </Alert>
        )}

        <Stack gap="sm">
          <CheckRow
            severity={r.hasAppId && r.hasAppSecret ? 'ok' : 'blocked'}
            label="Meta app"
            detail={
              r.hasAppId && r.hasAppSecret
                ? 'App ID and secret are set.'
                : !r.hasAppId
                  ? 'No App ID — every connect path is unavailable.'
                  : 'App ID is set but the secret is missing.'
            }
          />
          <CheckRow
            severity={r.hasEsConfigId ? 'ok' : 'blocked'}
            label="Embedded Signup configuration"
            detail={
              r.hasEsConfigId
                ? 'Set — vendors get the two-minute popup.'
                : 'Not set — blocks the recommended WhatsApp path for every vendor.'
            }
          />
          <CheckRow
            severity={r.hasFacebookConfigId ? 'ok' : 'degraded'}
            label="Messenger configuration"
            detail={
              r.hasFacebookConfigId
                ? 'Set.'
                : 'Not set — Messenger falls back to the slower redirect sign-in.'
            }
          />
          <CheckRow
            severity={r.hasInstagramConfigId ? 'ok' : 'degraded'}
            label="Instagram configuration"
            detail={
              r.hasInstagramConfigId
                ? 'Set.'
                : 'Not set — Instagram falls back to the slower redirect sign-in.'
            }
          />
          <CheckRow
            severity={r.hasInstagramApp ? 'ok' : 'degraded'}
            label="Instagram app (Business Login)"
            detail={
              r.hasInstagramApp
                ? 'Set — standalone Instagram accounts can connect.'
                : "Not set — Instagram accounts without a linked Facebook Page can't connect."
            }
          />
          <CheckRow
            severity={r.coexistenceEnabled ? 'ok' : 'degraded'}
            label="Coexistence"
            detail={
              r.coexistenceEnabled
                ? 'On — vendors can keep the number they already use.'
                : 'Off — vendors already running the WhatsApp Business app must take a new number.'
            }
          />
          <CheckRow
            severity={r.onboardingV2Enabled ? 'ok' : 'degraded'}
            label="Guided onboarding"
            detail={
              r.onboardingV2Enabled
                ? 'On — new vendors go through the step-by-step setup.'
                : 'Off — new vendors land on the dashboard with no setup prompt.'
            }
          />
        </Stack>

        {!blocked && r.vendorsWithNoChannel > 0 && (
          <Text size="xs" c="dimmed">
            {r.vendorsWithNoChannel} of {r.vendorsTotal} vendors have no channel connected. The
            platform isn&apos;t the blocker — they haven&apos;t finished setup.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
